// @flow
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/styles';
import {
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@material-ui/core';

import {usePage} from '../../../page/utils';
import {colors} from '../../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useStoreInfo} from '../../../store/utils';

import HeaderTitle from '../components/HeaderTitle/HeaderTitle';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';

import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import {useContactTypes} from '../../../information/utils';
import {useCustomerComplaintSettings} from '../../../customer-complaints/utils/useCustomerComplaintSetting';
import {useInformationFields} from '../../../customer-complaints/utils/useInformationFields';
import {usesInternalComplaintModule} from '../../../customer-complaints/constants';
import {useStoreCountry} from '../../../country/utils';
import CustomChip from '../../../../elements/CustomChip';
import UpgradeIcon from '../../../../elements/icons/UpgradeIcon';

type Props = {
  fetchStoreComplaintsConfig: () => void,
  resetState: () => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    background: '#FFFFFF',
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    '&:not(:last-child)': {
      marginBottom: theme.spacing(3),
    },
  },
  innerSubtitle: {
    color: colors.grey,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
  },
  radioGroup: {
    gap: theme.spacing(1),
    flexDirection: 'row',
  },
  radioOption: {
    borderRadius: '8px',
    padding: theme.spacing(1, 2),
    margin: 0,
    '&:hover': {
      backgroundColor: colors.grey100,
    },
  },
  radioLabel: {
    fontSize: '14px',
    fontWeight: 500,
  },
  formSection: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: colors.grey100,
    borderRadius: '8px',
    border: `1px solid ${colors.grey300}`,
  },
  infoText: {
    color: colors.grey700,
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: theme.spacing(1),
  },
  emailText: {
    color: colors.primary,
    fontWeight: 600,
  },
  complaintsForm: {
    width: '100%',
    flexDirection: 'column',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  complaintsFormSubmit: {
    maxWidth: '200px',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  infoLink: {
    color: colors.purple,
    textDecoration: 'none',
    fontWeight: 600,
  },
  informationFieldsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${colors.grey300}`,
  },
}));

function Complaints(props: Props) {
  const classes = useStyles();
  const store = useStoreInfo();
  const country = useStoreCountry();
  const forcesInternalComplaintModule = usesInternalComplaintModule(
    country?.code
  );

  const hasCustomerComplaintFeature = useIsFeatureInTier('CUSTOMER_COMPLAINT');

  const contactTypes = useContactTypes();

  const {
    setting,
    loading: settingLoading,
    saving: settingSaving,
    saveSetting,
  } = useCustomerComplaintSettings();

  const email = contactTypes?.find(
    contactType => contactType.contactType.name === 'CORPORATE_EMAIL'
  )?.data;

  const [complaintsFormUrlError, setComplaintsFormUrlError] = useState('');
  const [complaintsFormUrl, setComplaintsFormUrl] = useState('');
  const [formType, setFormType] = useState('external');
  const [enabled, setEnabled] = useState(false);

  const effectiveFormType = forcesInternalComplaintModule
    ? 'internal'
    : formType;

  const {
    informationFields,
    updateFieldValue,
    getInformationFieldsPayload,
  } = useInformationFields(setting, country?.code);

  useEffect(() => {
    if (forcesInternalComplaintModule) {
      setFormType('internal');
    }
  }, [forcesInternalComplaintModule]);

  useEffect(() => {
    if (setting) {
      setFormType(
        forcesInternalComplaintModule
          ? 'internal'
          : setting.customerComplaintType === 'LINK'
          ? 'external'
          : 'internal'
      );
      setComplaintsFormUrl(setting.formUrl || '');
      setEnabled(setting.enabled || false);
    }
  }, [setting, forcesInternalComplaintModule]);

  const handleComplaintsFormUrlChange = (formUrl: string) => {
    setComplaintsFormUrlError('');
    const trimmedFormUrl = formUrl.trim();

    if (trimmedFormUrl.length === 0) {
      setComplaintsFormUrl('');
      return;
    }
    setComplaintsFormUrl(trimmedFormUrl);
    try {
      new URL(formUrl);
      setComplaintsFormUrl(formUrl);
    } catch (error) {
      setComplaintsFormUrlError('El link no es válido');
    }
  };

  const handleSaveComplaintsSetting = async () => {
    if (effectiveFormType === 'external' && !!complaintsFormUrlError) return;
    if (!store?.id) return;

    try {
      const settingData = {
        customerComplaintType:
          effectiveFormType === 'external' ? 'LINK' : 'FORM',
        enabled,
      };

      if (effectiveFormType === 'external') {
        settingData.formUrl = complaintsFormUrl.trim();
      } else {
        settingData.informationFields = getInformationFieldsPayload();
      }

      await saveSetting(settingData);
    } catch (error) {
      console.error('Error saving complaints setting:', error);
    }
  };

  const handleFormTypeChange = (event: any) => {
    if (forcesInternalComplaintModule) return;

    const newType = event.target.value;
    setFormType(newType);

    if (newType === 'internal') {
      setComplaintsFormUrlError('');
    }
  };

  const handleEnabledChange = (newEnabled: boolean) => {
    setEnabled(newEnabled);

    if (!store?.id) return;

    // Stores not in INTERNAL_COMPLAINT_MODULE_COUNTRY_CODES without the
    // internal feature fall back to external LINK. Configured countries
    // always use the internal FORM module.
    if (
      !forcesInternalComplaintModule &&
      effectiveFormType === 'internal' &&
      !hasCustomerComplaintFeature
    ) {
      const settingData = {
        customerComplaintType: 'LINK',
        enabled: newEnabled,
      };

      saveSetting(settingData);
      return;
    }

    const settingData = {
      customerComplaintType: effectiveFormType === 'external' ? 'LINK' : 'FORM',
      enabled: newEnabled,
    };

    if (effectiveFormType === 'external') {
      settingData.formUrl = complaintsFormUrl.trim();
    } else {
      settingData.informationFields = getInformationFieldsPayload();
    }

    saveSetting(settingData);
  };

  return (
    <div className={classes.root}>
      {(settingSaving || settingLoading) && <LoadingSpinner />}

      <HeaderTitle
        title="Libro de reclamaciones"
        description="Permite a los clientes enviar reclamos sobre productos o servicios de manera oficial"
        enabledKeyName="complaintsSetting"
        enabled={enabled}
        // disabled={!hasComplaintsFeature}
        onChangeEnabled={handleEnabledChange}
      />
      <div style={{width: '100%'}}>
        <Typography className={classes.innerSubtitle}>
          Configura cómo los clientes pueden enviar sus reclamos desde tu tienda
          online.
        </Typography>

        {!forcesInternalComplaintModule && (
          <RadioGroup
            className={classes.radioGroup}
            value={formType}
            onChange={handleFormTypeChange}
          >
            <FormControlLabel
              value="external"
              control={<Radio color="primary" />}
              label={
                <Typography className={classes.radioLabel}>
                  Usar link de formulario externo
                </Typography>
              }
              className={classes.radioOption}
            />
            <FormControlLabel
              value="internal"
              control={<Radio color="primary" />}
              label={
                <Typography className={classes.radioLabel}>
                  Usar formulario interno de la plataforma
                  <CustomChip
                    label="Nuevo"
                    color="primary"
                    size="small"
                    style={{marginLeft: 8}}
                  />
                </Typography>
              }
              className={classes.radioOption}
            />
          </RadioGroup>
        )}

        {effectiveFormType === 'internal' ? (
          <div className={classes.formSection}>
            <Typography className={classes.infoText}>
              Formulario interno activado
            </Typography>
            <Typography className={classes.infoText}>
              Los clientes podrán completar un formulario directamente en tu
              tienda online. Los reclamos se guardaran en tu{' '}
              <span className={classes.emailText}>backoffice</span> y se
              enviarán automáticamente al siguiente correo:
            </Typography>
            <Typography className={classes.emailText}>
              {email ? email : 'No se ha configurado un correo electrónico'}
            </Typography>

            {informationFields.length > 0 && (
              <div className={classes.informationFieldsSection}>
                <Typography className={classes.infoText}>
                  <strong>Información adicional (opcional)</strong>
                </Typography>
                <Typography className={classes.innerSubtitle}>
                  Esta información se mostrará en el formulario de reclamos.
                  Deja en blanco los que no apliquen.
                </Typography>
                {informationFields.map(field => (
                  <CustomTextField
                    key={field.label}
                    label={field.label}
                    value={field.value}
                    onChange={e =>
                      updateFieldValue(field.label, e.target.value)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={classes.formSection}>
            <Typography className={classes.infoText}>
              <strong>Formulario externo</strong>
            </Typography>
            <Typography className={classes.infoText}>
              Coloca el link del formulario de reclamo:
            </Typography>
            <Typography className={classes.infoText}>
              Si no tienes un link de formulario de reclamo, el reclamo llegará
              al siguiente correo:{' '}
              <span className={classes.emailText}>
                {email ? email : 'admin@tiendada.com'}
              </span>
            </Typography>
            <form
              className={classes.complaintsForm}
              onSubmit={e => {
                e.preventDefault();
                handleSaveComplaintsSetting();
              }}
            >
              <CustomTextField
                placeholder="https://docs.google.com/forms"
                onChange={e => handleComplaintsFormUrlChange(e.target.value)}
                value={complaintsFormUrl}
                helperText={complaintsFormUrlError}
                error={!!complaintsFormUrlError}
              />
              <CustomButton
                type="submit"
                variant="outlined"
                fullWidth
                disabled={
                  effectiveFormType === 'external' && !!complaintsFormUrlError
                }
                className={classes.complaintsFormSubmit}
                color="primary"
              >
                Guardar configuración
              </CustomButton>
            </form>
            <Typography className={classes.infoText} style={{marginTop: 16}}>
              Usa este formulario de ejemplo. Duplica esta plantilla y edita tus
              datos.{' '}
              <a
                href="https://drive.google.com/drive/folders/1SXR00j-wHgu-KGGTjUp-bKWyGZhnu9Fx"
                className={classes.infoLink}
                target="_blank"
                rel="noreferrer noopener"
              >
                Clic aquí.
              </a>
              <br />
              ¿Cómo duplicar la plantilla?{' '}
              <a
                href="https://www.loom.com/share/ab039b6b9c60422ab14a0505b89c1411?sid=a66214c9-ecf1-4682-b84b-5b781688703d"
                className={classes.infoLink}
                target="_blank"
                rel="noreferrer noopener"
              >
                Ver tutorial.
              </a>
            </Typography>
          </div>
        )}

        {effectiveFormType === 'internal' && (
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              gap: 16,
              alignItems: 'center',
            }}
          >
            <CustomButton
              variant="outlined"
              color="primary"
              onClick={handleSaveComplaintsSetting}
              className={classes.complaintsFormSubmit}
              disabled={!hasCustomerComplaintFeature}
            >
              Guardar configuración
            </CustomButton>

            <CustomChip
              label={
                hasCustomerComplaintFeature ? (
                  'Nuevo'
                ) : (
                  <>
                    Mejorar Plan
                    <UpgradeIcon width={18} height={18} />
                  </>
                )
              }
              color="primary"
              size="small"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default usePage<Props>(Complaints, 'Complaints Book Configuration');
